import styled from '@emotion/styled'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { colors } from '@/styles/theme'
import { ColorSelector } from '@/components/atoms/ColorSelector'
import { SexSelector } from '@/components/atoms/SexSelector'
import { VoiceChatSelector } from '@/components/atoms/VoiceChatSelector'
import {
  InputText,
  InputSelector,
  OptionsPlaytime,
} from '@/components/atoms/Inputs'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import { Platform } from '@/firebase/firestore/Platform'
import { Sex } from '@/firebase/firestore/Sex'
import { ValorantRank } from '@/firebase/firestore/ValorantRank'
import { ValorantSelector } from './ValorantSelector'
import { TemplateSelector } from '@/components/atoms/TemplateSelector'
import { useLocale } from '@/hooks/useLocale'
import Head from 'next/head'
import { ProfileCardEditorDescription } from '@/components/atoms/ProfileCardEditorDescription'
import { ProfileEditorAppBanner } from '@/components/organisms/Banner/ProfileEditorAppBanner'
import { CREATOR, creatorMapTargetId, CREATORS, mapCreatorInfo } from './config'
import { Valorant1 } from './Valorant1'
import { Valorant2 } from './Valorant2'
import { Valorant3 } from './Valorant3'
import { Valorant4 } from './Valorant4'
import { Valorant5 } from './Valorant5'
import { Valorant6 } from './Valorant6'
import { Valorant7 } from './Valorant7'
import { Valorant8 } from './Valorant8'
import { Valorant9 } from '../Valorant9'
import { Valorant10 } from '../Valorant10'
import { PlatformSelector } from '@/components/atoms/PlatformSelector'

/**
 * プラットフォーム事にフォームが違う
 *
 */

type FormField = {
  id: string
  name: string
  playStyle: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  valorantFavoriteWeapon: string
  valorantMap: string
  valorantRole: string
  ng: string
  hitokoto: string
  valorantRank: ValorantRank
  valorantAgeRange: string
  voiceChat: string
  age: string
}

type Props = {
  onCreate: (
    data: FormField & { selectedLegends: number[] },
    targetId: string,
    selectedSex: Sex,
    selectedPlatform: Array<Platform>,
    selectedVoiceChat: Array<VoiceChat>
  ) => void
  submitButtonDisable: boolean
  creatorParam: string | null
}

const FONT_COLORS = [
  '#FD8080',
  '#FD7C24',
  '#29DDF2',
  '#C05AE0',
  '#7AA852',
  '#4C4C4C',
]

const creatorMapColors: Record<CREATOR, string[]> = {
  koharubiyori_51: ['#C3F3D9', '#CAF5FC', '#F5F8BB', '#FBDEEC'],
  natsuko: ['#9C9897', '#808A9B', '#D3D0CB', '#ACB8A1'],
  Nonax299: [
    '#E4CBB9',
    '#DB8CB5',
    '#B29DCA',
    '#80A9D0',
    '#74986F',
    '#D47978',
    '#DDBC77',
    '#626262',
  ],
  bokuwanco: ['#FDC9C9', '#FBC4E4', '#E9C9FC', '#C5DBFC', '#B8E8E7'],
  nawowcow: ['#DEC489', '#F0AB7C', '#86C3D6'],
  tsunaegg: ['#C0BEA5', '#82BDBA', '#D186B4', '#B6DB8F'],
  // shirobe_xx: ['#FFD2DF', '#ABA49E', '#E5E5E5', '#8AB7C2'],
  cott0n_x: ['#FCE3F1', '#D3EFF2', '#DCE1BE', '#F3CEB0'],
  purimipurioka: ['#FCD9E5', '#DCFFFE', '#FCB6CB'],
  RiU__Sky: [
    '#91D8D6',
    '#000000',
    '#BFA189',
    '#C9E9BA',
    '#BA4345',
    '#6C8DAC',
    '#FFCE98',
    '#C7B7D7',
    '#E8C2C7',
    '#8DBCB7',
    '#F8E579',
    '#FCFB92',
  ],
}

const creatorMapCharacters: Record<CREATOR, string[]> = {
  koharubiyori_51: [
    '/static/images/characters/valorant1/0.jpg',
    '/static/images/characters/valorant1/1.jpg',
    '/static/images/characters/valorant1/2.jpg',
    '/static/images/characters/valorant1/3.jpg',
    '/static/images/characters/valorant1/4.jpg',
    '/static/images/characters/valorant1/5.jpg',
    '/static/images/characters/valorant1/6.jpg',
    '/static/images/characters/valorant1/7.jpg',
    '/static/images/characters/valorant1/8.jpg',
    '/static/images/characters/valorant1/9.jpg',
    '/static/images/characters/valorant1/10.jpg',
    '/static/images/characters/valorant1/11.jpg',
    '/static/images/characters/valorant1/12.jpg',
    '/static/images/characters/valorant1/13.jpg',
    '/static/images/characters/valorant1/14.jpg',
    '/static/images/characters/valorant1/15.jpg',
    '/static/images/characters/valorant1/16.jpg',
    '/static/images/characters/valorant1/17.jpg',
    '/static/images/characters/valorant1/18.jpg',
  ],
  natsuko: [
    '/static/images/characters/valorant2/0.jpg',
    '/static/images/characters/valorant2/1.jpg',
    '/static/images/characters/valorant2/2.jpg',
    '/static/images/characters/valorant2/3.jpg',
    '/static/images/characters/valorant2/4.jpg',
    '/static/images/characters/valorant2/5.jpg',
    '/static/images/characters/valorant2/6.jpg',
    '/static/images/characters/valorant2/7.jpg',
    '/static/images/characters/valorant2/8.jpg',
    '/static/images/characters/valorant2/9.jpg',
    '/static/images/characters/valorant2/10.jpg',
    '/static/images/characters/valorant2/11.jpg',
    '/static/images/characters/valorant2/12.jpg',
    '/static/images/characters/valorant2/13.jpg',
    '/static/images/characters/valorant2/14.jpg',
    '/static/images/characters/valorant2/15.jpg',
    '/static/images/characters/valorant2/16.jpg',
    '/static/images/characters/valorant2/17.jpg',
    '/static/images/characters/valorant2/18.jpg',
  ],
  Nonax299: Array(24)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/valorant3/${index}.png`
    }),
  bokuwanco: Array(22)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/valorant4/${index}.png`
    }),
  nawowcow: Array(24)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/valorant5/${index}.png`
    }),
  tsunaegg: Array(24)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/valorant6/${index}.png`
    }),
  // shirobe_xx: Array(24)
  //   .fill(0)
  //   .map((_, index) => {
  //     return `/static/images/characters/valorant7/${index}.png`
  //   }),
  cott0n_x: Array(24)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/valorant8/${index}.png`
    }),
  purimipurioka: Array(25)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/valorant9/${index}.png`
    }),
  RiU__Sky: [
    ...Array(8)
      .fill(0)
      .map((_, index) => {
        return `/static/images/characters/valorant10/d_${index}.png`
      }),
    ...Array(7)
      .fill(0)
      .map((_, index) => {
        return `/static/images/characters/valorant10/i_${index}.png`
      }),
    ...Array(6)
      .fill(0)
      .map((_, index) => {
        return `/static/images/characters/valorant10/c_${index}.png`
      }),
    ...Array(6)
      .fill(0)
      .map((_, index) => {
        return `/static/images/characters/valorant10/s_${index}.png`
      }),
  ],
}

const templates = Object.keys(mapCreatorInfo).map((creatorName) => {
  return {
    imagePath: mapCreatorInfo[creatorName]['profileCardImageURL'],
    value: creatorName,
  }
})

export const EditorValorant: React.FC<Props> = ({
  onCreate,
  submitButtonDisable,
  creatorParam,
}) => {
  const { t } = useLocale()
  const { handleSubmit, register, watch } = useForm<FormField>({
    defaultValues: {
      playTimeWeekDayBegin: '18',
      playTimeWeekDayEnd: '20',
      playTimeWeekEndBegin: '18',
      playTimeWeekEndEnd: '20',
    },
  })
  const fieldVal = watch()

  const [selectedCreator, setSelectedCreator] = useState<CREATOR>(
    CREATORS.includes(creatorParam as any)
      ? (creatorParam as CREATOR)
      : CREATORS[0]
  )
  useEffect(() => {
    if (!selectedCreator) return
    const url = new URL(window.location.href)
    url.searchParams.set('creator', selectedCreator)
    window.history.pushState(null, '', url.toString())
  }, [selectedCreator])

  const [selectedColor, setColor] = useState(0)
  const onChangeColor = useCallback((selectedIndex: number) => {
    setColor(selectedIndex)
  }, [])
  const [selectedFontColor, setFontColor] = useState(0)
  const onChangeFontColor = useCallback((selectedIndex: number) => {
    setFontColor(selectedIndex)
  }, [])
  const [selectedSex, setSex] = useState<Sex>(0)
  const onChangeSex = useCallback((selectedIndex: Sex) => {
    setSex(selectedIndex)
  }, [])

  const [selectedPlatform, setPlatform] = useState<Array<Platform>>([0])
  const onChangePlatform = useCallback((selectedIndex: Array<Platform>) => {
    setPlatform(selectedIndex)
  }, [])

  const [selectedVoiceChat, setVoiceChat] = useState<Array<VoiceChat>>([])
  const onChangeVoiceChat = useCallback((selectedIndex: Array<VoiceChat>) => {
    setVoiceChat(selectedIndex)
  }, [])

  const [selectedLegends, setLegends] = useState<number[]>([])
  const onChangeLegends = useCallback((selectedIndex: number[]) => {
    setLegends(selectedIndex)
  }, [])

  const handleCreate = (data: FormField) => {
    onCreate(
      { ...data, selectedLegends },
      creatorMapTargetId[selectedCreator],
      selectedSex,
      selectedPlatform,
      selectedVoiceChat
    )
  }

  const jsonLdConfigs = [
    {
      '@context': 'http://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'gamee',
          item: 'https://gamee.games/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '【VALORANT(ヴァロラント)】自己紹介カード作成ツール',
          item: 'https://gamee.games/me/profiles/create/valorant',
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': 'https://gamee.games/me/profiles/create/valorant',
      },
      headline: '【VALORANT(ヴァロラント)】自己紹介カード作成ツール',
      description:
        'VALORANT(ヴァロラント)のかわいい自己紹介カードが簡単に作成・編集できるツールです！🥳🎉 プレビューを見ながら文字入れをしてあなただけの自己紹介カードをつくろう！',
      articleBody:
        'VALORANT(ヴァロラント)のかわいい自己紹介カードが簡単に作成・編集できるツールです！🥳🎉 テンプレートを選ぶ カードの背景色を選ぶ 文字色を選ぶ なまえ ID 性別 プラットフォーム ボイスチャット プレイ時間帯(平日) プレイ時間帯(土日) 好きな武器 よく使うレジェンド K/D 最高ダメージ ランク ひとこと これでつくる！',
      image: ['https://gamee.games/static/images/promotes/valorant.jpg'],
      datePublished: '2022-11-20T20:34:21+09:00',
      dateModified: `${new Date().getFullYear()}-${(
        '0' +
        (new Date().getMonth() + 1)
      ).slice(-2)}-${('0' + new Date().getDate()).slice(-2)}T20:34:21+09:00`,
      author: {
        '@type': 'Person',
        name: 'あやの',
      },
      publisher: {
        '@type': 'Organization',
        name: 'gamee',
        logo: {
          '@type': 'ImageObject',
          url: 'https://gamee.games/static/images/icn_ayano.jpeg',
        },
      },
    },
  ]

  return (
    <>
      <Head>
        <script type="application/ld+json">
          {JSON.stringify(jsonLdConfigs)}
        </script>
      </Head>

      <PreviewContainer>
        <PreviewCardContainer>
          {selectedCreator === 'koharubiyori_51' && (
            <Valorant1
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontColor={FONT_COLORS[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              valorantFavoriteWeapon={fieldVal.valorantFavoriteWeapon}
              valorantRank={fieldVal.valorantRank}
              hitokoto={fieldVal.hitokoto}
              agents={selectedLegends}
            />
          )}
          {selectedCreator === 'natsuko' && (
            <Valorant2
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontColor={FONT_COLORS[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              valorantRank={fieldVal.valorantRank}
              hitokoto={fieldVal.hitokoto}
              agents={selectedLegends}
            />
          )}
          {selectedCreator === 'Nonax299' && (
            <Valorant3
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontColor={FONT_COLORS[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              valorantRank={fieldVal.valorantRank}
              hitokoto={fieldVal.hitokoto}
              agents={selectedLegends}
            />
          )}
          {selectedCreator === 'bokuwanco' && (
            <Valorant4
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontColor={FONT_COLORS[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              valorantRank={fieldVal.valorantRank}
              valorantFavoriteWeapon={fieldVal.valorantFavoriteWeapon}
              ng={fieldVal.ng}
              hitokoto={fieldVal.hitokoto}
              agents={selectedLegends}
            />
          )}
          {selectedCreator === 'nawowcow' && (
            <Valorant5
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontColor={FONT_COLORS[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              valorantRank={fieldVal.valorantRank}
              valorantMap={fieldVal.valorantMap}
              valorantRole={fieldVal.valorantRole}
              hitokoto={fieldVal.hitokoto}
              agents={selectedLegends}
            />
          )}
          {selectedCreator === 'tsunaegg' && (
            <Valorant6
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontColor={FONT_COLORS[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              valorantFavoriteWeapon={fieldVal.valorantFavoriteWeapon}
              valorantRank={fieldVal.valorantRank}
              hitokoto={fieldVal.hitokoto}
              agents={selectedLegends}
            />
          )}
          {/* {selectedCreator === 'shirobe_xx' && (
          //   <Valorant7
          //     targetId={creatorMapTargetId[selectedCreator]}
          //     uid={fieldVal.id}
          //     fontColor={FONT_COLORS[selectedFontColor]}
          //     playStyle={fieldVal.playStyle}
          //     platform={selectedPlatform}
          //     name={fieldVal.name}
          //     sex={selectedSex}
          //     templateColor={selectedColor as 0 | 1 | 2 | 3}
          //     voiceChat={selectedVoiceChat}
          //     playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
          //     playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
          //     playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
          //     playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
          //     valorantFavoriteWeapon={fieldVal.valorantFavoriteWeapon}
          //     valorantRank={fieldVal.valorantRank}
          //     hitokoto={fieldVal.hitokoto}
          //     agents={selectedLegends}
          //   />
          // )} */}
          {selectedCreator === 'cott0n_x' && (
            <Valorant8
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontColor={FONT_COLORS[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              valorantFavoriteWeapon={fieldVal.valorantFavoriteWeapon}
              valorantRank={fieldVal.valorantRank}
              hitokoto={fieldVal.hitokoto}
              agents={selectedLegends}
            />
          )}
          {selectedCreator === 'purimipurioka' && (
            <Valorant9
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontColor={FONT_COLORS[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              valorantFavoriteWeapon={fieldVal.valorantFavoriteWeapon}
              valorantRank={fieldVal.valorantRank}
              hitokoto={fieldVal.hitokoto}
              valorantAgeRange={fieldVal.valorantAgeRange}
              agents={selectedLegends}
            />
          )}
          {selectedCreator === 'RiU__Sky' && (
            <Valorant10
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontColor={FONT_COLORS[selectedFontColor]}
              playStyle={fieldVal.playStyle}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              valorantFavoriteWeapon={fieldVal.valorantFavoriteWeapon}
              valorantRank={fieldVal.valorantRank}
              hitokoto={fieldVal.hitokoto}
              agents={selectedLegends}
              age={Number(fieldVal.age)}
            />
          )}
        </PreviewCardContainer>
        <ProfileEditorAppBanner game="valorant" />
      </PreviewContainer>
      <DescriptionSection>
        <Time>
          <time
            dateTime={'2022-11-20T20:34:21+09:00'}
            itemProp="datepublished"
          />
          <time dateTime={new Date().toISOString()} itemProp="modified">
            {t.最終更新} : {new Date().getFullYear()}年
            {new Date().getMonth() + 1}月{new Date().getDate()}日
          </time>
        </Time>
        <H1Text>{t.VALORANTかわいい自己紹介カード作成ツール}</H1Text>
        <H2Text>{t.VALORANTかわいい自己紹介カード作成ツール説明文}</H2Text>
        <ProfileCardEditorDescription />
      </DescriptionSection>
      <FormContainer>
        <form
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
          onSubmit={handleSubmit(handleCreate)}
        >
          <FormItemContainer>
            <SectionHeader>{t.テンプレートを選ぶ}</SectionHeader>
            <TemplateSelector
              values={templates}
              onChange={setSelectedCreator as any}
              value={selectedCreator}
            />
          </FormItemContainer>
          <FormItemContainer>
            <SectionHeader>{t.カードの背景色を選ぶ}</SectionHeader>
            <ColorSelector
              // templateで可変
              colors={creatorMapColors[selectedCreator]}
              value={selectedColor}
              onChange={onChangeColor}
            />
          </FormItemContainer>
          <FormItemContainer>
            <SectionHeader>{t.文字色を選ぶ}</SectionHeader>
            <ColorSelector
              colors={FONT_COLORS}
              value={selectedFontColor}
              onChange={onChangeFontColor}
            />
          </FormItemContainer>
          <FormItemContainer>
            <SectionHeader>{t.なまえ}</SectionHeader>
            <InputText {...register('name', { maxLength: 20 })} />
          </FormItemContainer>
          {(selectedCreator === 'koharubiyori_51' ||
            selectedCreator === 'Nonax299' ||
            selectedCreator === 'nawowcow') && (
            <FormItemContainer>
              <SectionHeader>{t.ID}</SectionHeader>
              <InputText {...register('id', { maxLength: 20 })} />
            </FormItemContainer>
          )}
          <FormItemContainer>
            <SectionHeader>{t.性別}</SectionHeader>
            <SexSelector val={selectedSex} onChange={onChangeSex} />
          </FormItemContainer>
          {selectedCreator === 'nawowcow' && (
            <FormItemContainer>
              <SectionHeader>{t.ロール}</SectionHeader>
              <InputText {...register('valorantRole', { maxLength: 20 })} />
            </FormItemContainer>
          )}
          {/* <FormItemContainer>
            <SectionHeader>プラットフォーム</SectionHeader>
            <PlatformSelector
              onChange={onChangePlatform}
              value={selectedPlatform}
              activeValues={[0, 1, 2, 3]}
            />
          </FormItemContainer> */}
          {selectedCreator === 'RiU__Sky' && (
            <FormItemContainer>
              <SectionHeader>年齢層</SectionHeader>
              <InputSelector {...register('age')}>
                <option value="0">20↓</option>
                <option value="20">20↑</option>
                <option value="25">25↑</option>
                <option value="30">30↑</option>
                <option value="35">35↑</option>
                <option value="40">40↑</option>
                <option value="45">45↑</option>
                <option value="50">50↑</option>
              </InputSelector>
            </FormItemContainer>
          )}

          <FormItemContainer>
            <SectionHeader>{t.プレイ時間帯平日}</SectionHeader>
            <InputSelector {...register('playTimeWeekDayBegin')}>
              <OptionsPlaytime />
            </InputSelector>
            <TimeSelectorText>から</TimeSelectorText>
            <InputSelector {...register('playTimeWeekDayEnd')}>
              <OptionsPlaytime />
            </InputSelector>
          </FormItemContainer>
          <FormItemContainer>
            <SectionHeader>{t.プレイ時間帯土日}</SectionHeader>
            <InputSelector {...register('playTimeWeekEndBegin')}>
              <OptionsPlaytime />
            </InputSelector>
            <TimeSelectorText>から</TimeSelectorText>
            <InputSelector {...register('playTimeWeekEndEnd')}>
              <OptionsPlaytime />
            </InputSelector>
          </FormItemContainer>
          {(selectedCreator === 'koharubiyori_51' ||
            selectedCreator === 'Nonax299' ||
            selectedCreator === 'nawowcow' ||
            selectedCreator === 'tsunaegg' ||
            selectedCreator === 'RiU__Sky') && (
            <FormItemContainer>
              <SectionHeader>ボイスチャット</SectionHeader>
              <VoiceChatSelector
                onChange={onChangeVoiceChat}
                value={selectedVoiceChat}
                activeValue={
                  selectedCreator === 'RiU__Sky' ? [97, 98, 4] : [3, 1, 4]
                }
              />
            </FormItemContainer>
          )}
          <FormItemContainer>
            <SectionHeader>{t.よく使うエージェント}</SectionHeader>
            <ValorantSelector
              values={creatorMapCharacters[selectedCreator]}
              onChange={onChangeLegends}
            />
          </FormItemContainer>
          {(selectedCreator === 'koharubiyori_51' ||
            selectedCreator === 'bokuwanco') && (
            <FormItemContainer>
              <SectionHeader>{t.好きな武器}</SectionHeader>
              <InputText
                {...register('valorantFavoriteWeapon', { maxLength: 100 })}
              />
            </FormItemContainer>
          )}
          {selectedCreator === 'nawowcow' && (
            <FormItemContainer>
              <SectionHeader>{t.好きなマップ}</SectionHeader>
              <InputText {...register('valorantMap', { maxLength: 100 })} />
            </FormItemContainer>
          )}
          <FormItemContainer>
            <SectionHeader>{t.プレイスタイル}</SectionHeader>
            <InputText {...register('playStyle', { maxLength: 50 })} />
          </FormItemContainer>
          <FormItemContainer>
            <SectionHeader>{t.ランク}</SectionHeader>
            <InputSelector {...register('valorantRank')}>
              <option value="iron">{t.アイアン}</option>
              <option value="bronze">{t.ブロンズ}</option>
              <option value="silver">{t.シルバー}</option>
              <option value="gold">{t.ゴールド}</option>
              <option value="platinum">{t.プラチナ}</option>
              <option value="diamond">{t.ダイヤ}</option>
              <option value="ascendant">{t.アセンダント}</option>
              <option value="immortal">{t.イモータル}</option>
              <option value="radiant">{t.レディアント}</option>
            </InputSelector>
          </FormItemContainer>
          {(selectedCreator === 'cott0n_x' ||
            selectedCreator === 'purimipurioka' ||
            selectedCreator === 'RiU__Sky') && (
            <FormItemContainer>
              <SectionHeader>{t.プラットフォーム}</SectionHeader>
              <PlatformSelector
                onChange={onChangePlatform}
                value={selectedPlatform}
                activeValues={
                  selectedCreator === 'cott0n_x'
                    ? [0, 1, 2]
                    : selectedCreator === 'RiU__Sky'
                    ? [0, 1, 2, 4]
                    : [0, 1, 2]
                }
              />
            </FormItemContainer>
          )}
          {selectedCreator === 'purimipurioka' && (
            <FormItemContainer>
              <SectionHeader>{'世代'}</SectionHeader>
              <InputSelector {...register('valorantAgeRange')}>
                <option value="0">{'指定なし'}</option>
                <option value="10">{'10'}</option>
                <option value="20">{'20'}</option>
                <option value="30">{'30'}</option>
                <option value="40">{'40'}</option>
                <option value="50">{'50~'}</option>
              </InputSelector>
            </FormItemContainer>
          )}

          {selectedCreator === 'bokuwanco' && (
            <FormItemContainer>
              <SectionHeader>{'NG'}</SectionHeader>
              <InputText {...register('ng', { maxLength: 50 })} />
            </FormItemContainer>
          )}
          <FormItemContainer>
            <SectionHeader>{t.ひとこと}</SectionHeader>
            <InputText {...register('hitokoto', { maxLength: 50 })} />
          </FormItemContainer>

          <ButtonDescriptionText>
            {t.カードの作成にはSNS連携が必要です次の画面でログインをします}
          </ButtonDescriptionText>
          <Submit
            id="create_profile"
            type="submit"
            value={t.これでつくる}
            disabled={submitButtonDisable}
          />
        </form>
      </FormContainer>
    </>
  )
}

const PreviewContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
`

const PreviewCardContainer = styled.div`
  background-color: ${colors.bg.primary};
  box-shadow: 0 0 4px rgba(32, 32, 32, 0.1);
`

const FormContainer = styled.div`
  padding: 20px;
`

const FormItemContainer = styled.div`
  margin-bottom: 32px;
`

const DescriptionSection = styled.div`
  margin-top: 16px;
  margin-left: 16px;
  margin-right: 16px;
`

const Time = styled.div`
  color: ${colors.text.secondary};
  font-size: 12px;
`

const H1Text = styled.h1`
  margin-top: 12px;
  font-size: 12px;
  font-weight: bold;
  color: ${colors.text.primary};
  text-align: center;
  display: flex;
`

const H2Text = styled.h2`
  margin-top: 20px;
  font-size: 11px;
  font-weight: bold;
  color: ${colors.text.secondary};
  line-height: 18px;
  display: flex;
`

const SectionHeader = styled.div`
  margin-top: 20px;
  margin-bottom: 16px;
  font-weight: bold;
  font-size: 18px;
  color: ${colors.text.primary};
  display: flex;
`

const TimeSelectorText = styled.div`
  margin: 8px auto;
  color: ${colors.text.primary};
  font-size: 12px;
  font-weight: bold;
`

const ButtonDescriptionText = styled.h3`
  margin: auto;
  margin-top: 30px;
  width: 100%;
  font-size: 10px;
  color: ${colors.text.secondary};
  justify-content: center;
  display: flex;
`

const Submit = styled.input`
  margin-top: 12px;
  border-radius: 20px;
  border: 3px solid transparent;
  width: 100%;
  height: 40px;
  background-color: ${colors.button.primary};
  color: #fff;
  font-weight: bold;
  display: block;
`
